// Phase 9 R2 — Inventory table with row actions.
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, History, Plus, Sliders, Power } from 'lucide-react';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';
import { StockPill } from './StockPill';
import { cn } from '@/lib/utils';

export interface InventoryTableProps {
  items: InventoryItem[];
  isLoading: boolean;
  showAdjustments: boolean;
  showTransactions: boolean;
  onEdit: (item: InventoryItem) => void;
  onAdjust: (item: InventoryItem) => void;
  onRestock: (item: InventoryItem) => void;
  onHistory: (item: InventoryItem) => void;
  onToggleActive: (item: InventoryItem) => void;
}

const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toFixed(2)}`;
const fmtNum = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toString();

export function InventoryTable({
  items,
  isLoading,
  showAdjustments,
  showTransactions,
  onEdit,
  onAdjust,
  onRestock,
  onHistory,
  onToggleActive,
}: InventoryTableProps) {
  if (isLoading) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        Loading inventory…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-sm text-muted-foreground">
          No inventory items match the current filters. Add your first stocked part to get started.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Part #</TableHead>
          <TableHead>Brand</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Supplier</TableHead>
          <TableHead>Bin</TableHead>
          <TableHead className="text-right">Cost</TableHead>
          <TableHead className="text-right">Sell</TableHead>
          <TableHead className="text-right">On Hand</TableHead>
          <TableHead className="text-right">Reserved</TableHead>
          <TableHead className="text-right">Available</TableHead>
          <TableHead className="text-right">Reorder Pt</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const available = item.quantity_available ?? 0;
          const inactive = !item.is_active;
          return (
            <TableRow key={item.id} className={cn(inactive && 'opacity-60')}>
              <TableCell className="font-mono text-xs">{item.part_number}</TableCell>
              <TableCell>{item.brand ?? '—'}</TableCell>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>{item.category ?? '—'}</TableCell>
              <TableCell>{item.supplier ?? '—'}</TableCell>
              <TableCell>{item.bin_location ?? '—'}</TableCell>
              <TableCell className="text-right">{fmtMoney(item.unit_cost)}</TableCell>
              <TableCell className="text-right">{fmtMoney(item.unit_sell_default)}</TableCell>
              <TableCell className="text-right">{fmtNum(item.quantity_on_hand)}</TableCell>
              <TableCell className="text-right">{fmtNum(item.quantity_reserved)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <span>{fmtNum(item.quantity_available)}</span>
                  <StockPill available={available} reorderPoint={item.reorder_point ?? 0} />
                </div>
              </TableCell>
              <TableCell className="text-right">{fmtNum(item.reorder_point)}</TableCell>
              <TableCell>
                {item.is_active ? (
                  <Badge variant="secondary">Active</Badge>
                ) : (
                  <Badge variant="outline">Inactive</Badge>
                )}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(item)}>
                      <Pencil className="h-4 w-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    {showAdjustments && (
                      <>
                        <DropdownMenuItem onClick={() => onAdjust(item)}>
                          <Sliders className="h-4 w-4 mr-2" /> Adjust Quantity
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onRestock(item)}>
                          <Plus className="h-4 w-4 mr-2" /> Restock
                        </DropdownMenuItem>
                      </>
                    )}
                    {showTransactions && (
                      <DropdownMenuItem onClick={() => onHistory(item)}>
                        <History className="h-4 w-4 mr-2" /> Transaction History
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onToggleActive(item)}>
                      <Power className="h-4 w-4 mr-2" />
                      {item.is_active ? 'Deactivate' : 'Activate'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
